import { useAuth0 } from "@auth0/auth0-react";
import {
	ProseMirror,
	ProseMirrorDoc,
	useEditorEffect,
	useEditorEventListener,
} from "@handlewithcare/react-prosemirror";
import { collabKey } from "@stepwisehq/prosemirror-collab-commit/collab-commit";
import { useRouter } from "next/router";
import { Slice } from "prosemirror-model";
import { type EditorState, NodeSelection, TextSelection } from "prosemirror-state";
import { Decoration, DecorationSet, type EditorView } from "prosemirror-view";
import React, {
	type PropsWithChildren,
	createElement,
	forwardRef,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import {
	type DocumentMetadata,
	type PublishSettings,
	makeUrlPrefix,
} from "@moment/api-collab/api-types";
import { SCROLL_CONTAINER_ROOT_ID } from "@moment/design-system";
import { schema } from "@moment/prosemirror-schema";
import {
	env,
	resolvePageHref,
	useDocumentFromCurrentRoute,
	useEditorStateForCurrRoute,
	usePageFromCurrentRoute,
	usePresence,
	useWorkspaceFromCurrentRoute,
} from "@moment/state";
import { handleIframeNavigation, isRunningInIframe, makeToast, sendPostMessage } from "@moment/ui";

import {
	indicatorDisplayableInProseMirror,
	removeDataSrcImagesFromFragment,
} from "~/components/canvas/easel/prosemirror/helpers";
import { RuntimeProvider } from "~/components/canvas/easel/runtime/RuntimeProvider";
import { useCreateRuntime } from "~/components/canvas/easel/runtime/useCreateRuntime";
import {
	focusEditor,
	setTextPlaceholderStateBlurred,
	setTextPlaceholderStateFocused,
} from "~/components/canvas/editor/editor-controls";
import { useEaselProseMirrorCopyHandler } from "~/components/canvas/hooks/useEaselProseMirrorCopyHandler";
import { useLocationHash } from "~/components/canvas/hooks/useLocationHash";
import { useSendToCodeEditorStore } from "~/state/machines/code-editor";

import { CodeSnippetBlock } from "../easel/prosemirror/nodeViews/CodeSnippetBlock";
import { Image } from "../easel/prosemirror/nodeViews/Image/Image";
import { InlineImage } from "../easel/prosemirror/nodeViews/InlineImage/InlineImage";
import { LinkCodeSnippet } from "../easel/prosemirror/nodeViews/LinkCodeSnippet";
import { LinkPullRequest } from "../easel/prosemirror/nodeViews/LinkPullRequest";
import { PageLink } from "../easel/prosemirror/nodeViews/PageLink";
import { CodeBlockWrapper } from "../easel/prosemirror/nodeViews/codeBlock/CodeBlockWrapper";
import { Columns } from "../easel/prosemirror/nodeViews/columns/Columns";
import { ComputeCell } from "../easel/prosemirror/nodeViews/computeCell/ComputeCell";
import { TldrawEmbedWrapper } from "../easel/prosemirror/nodeViews/tldrawEmbed";
import { ToDoItem } from "../easel/prosemirror/nodeViews/toDoItem/ToDoItem";
import { PageLinkSuggestions } from "../easel/prosemirror/plugins/page-link-autocomplete";
import { useComputeCells } from "../hooks/useComputeCells";
import { useEaselDeleteComputeCell } from "../hooks/useEaselDeleteComputeCell";
import { useEaselInsertTemplateAtPos } from "../hooks/useEaselInsertTemplateAtPos";
import { useEaselProseMirrorDropHandler } from "../hooks/useEaselProseMirrorDropHandler";
import { useEaselProseMirrorPasteHandler } from "../hooks/useEaselProseMirrorPasteHandler";
import {
	INSERT_TLDRAW_EMBED_EVENT_ID,
	type InsertTldrawEmbedDetail,
} from "../properties-pane/components-tab/TldrawEmbedSection";
import type { ComponentTemplateItem } from "../properties-pane/components-tab/types";
import { getPresenceColor } from "./PresenceAnchor";
import { BottomMargin } from "./controls/BottomMargin";

export const insertComponentFromTemplateEventId = "moment-insert-component-from-template";

export type EditorProps = PropsWithChildren<{
	document: DocumentMetadata;
	editorState: EditorState;
	documentInteractionMode?: "static" | "interactive";
}>;

export const Editor = forwardRef<
	HTMLDivElement,
	PropsWithChildren<{ publishTemplate?: PublishSettings["template"] }>
>(({ publishTemplate }, editorContainerRef) => {
	const { documentId } = useDocumentFromCurrentRoute();
	const { pageId, pageMetadata } = usePageFromCurrentRoute();

	const { query } = useRouter();

	const sendToCodeEditor = useSendToCodeEditorStore();

	// Hooks
	const hash = useLocationHash();

	// Component state
	const [ranHashEffect, setRanHashEffect] = useState(false);

	useEffect(() => {
		// reset the hash effect when the page changes
		setRanHashEffect(false);
	}, [pageId]);

	useEffect(() => {
		// Handle hash URL navigation
		if (
			hash === "" ||
			pageMetadata === undefined ||
			// editorViewRef.current === null ||
			ranHashEffect
		) {
			return;
		}

		const id = hash.startsWith("#") ? hash.slice(1) : hash;
		const elementToScrollTo = document.getElementById(id);
		elementToScrollTo?.scrollIntoView();
		setRanHashEffect(true);
	}, [hash, pageMetadata, ranHashEffect]);

	const { compiledCells } = useComputeCells(documentId, pageId);

	const insertTemplateAtPos = useEaselInsertTemplateAtPos();

	const deleteComputeCell = useEaselDeleteComputeCell();
	useEditorEventListener("keydown", (view, event) => {
		const sel = view.state.selection;
		if (!(sel instanceof NodeSelection) || sel.node.type !== schema.nodes.compute_cell) {
			return;
		}

		// If a user has a compute cell selected and they press a letter key, we don't want to
		// delete the compute cell.
		if (
			event.key !== "Backspace" &&
			event.key !== "Delete" &&
			event.key !== "ArrowUp" &&
			event.key !== "ArrowDown" &&
			event.key !== "ArrowLeft" &&
			event.key !== "ArrowRight" &&
			!(event.ctrlKey || event.metaKey || event.altKey)
		) {
			event.preventDefault();
			return;
		}

		if (event.key !== "Backspace" && event.key !== "Delete") {
			return;
		}

		const id = sel.node.attrs["id"];
		if (!id || id === "") {
			return;
		}

		void deleteComputeCell(id);
	});

	useEditorEffect(
		(view) => {
			const handler = async (e: unknown) => {
				// NOTE: I am not sure how to make the types work here, so we'll just go with a type
				// assertion.
				const event = e as CustomEvent<ComponentTemplateItem>;
				const template = event.detail;

				view.dispatch(
					insertTemplateAtPos(view.state.tr, template, view.state.selection.$anchor.pos)
				);

				const sel = view.state.selection;
				if (
					sel instanceof NodeSelection &&
					sel.node.type === schema.nodes.compute_cell &&
					sel.node.attrs["id"] &&
					template.requiresCodePane === true
				) {
					sendToCodeEditor({
						type: "openTab",
						openCodePane: true,
						activePageID: pageId,
						cellID: sel.node.attrs["id"],
					});

					// Tell the page actor to immediately compute
					//pageActor?.send({ type: "page.compute" });
				}

				//
				// Focus the editor; user has clicked a button or dragged-and-dropped a component
				// and the editor will therefore not have the focus. `setTimeout` to perform the
				// focus right after the render loop has completed.
				//

				setTimeout(() => view.focus(), 0);
			};

			document.addEventListener(insertComponentFromTemplateEventId, handler);
			return () => document.removeEventListener(insertComponentFromTemplateEventId, handler);
		},
		[compiledCells, insertTemplateAtPos, pageId, sendToCodeEditor]
	);

	useEditorEffect((view) => {
		const handler = (e: unknown) => {
			const event = e as CustomEvent<InsertTldrawEmbedDetail>;
			const { pageId: targetPageId } = event.detail;

			const pos = view.state.selection.$anchor.pos;
			const embedNode = schema.nodes.tldraw_embed.create({
				jsonPath: `pages/${targetPageId}/index.json`,
				pageId: targetPageId,
			});

			let tr = view.state.tr.insert(pos, embedNode);

			const sel = tr.selection;
			const selectedNodeFrom = sel.$from.node();
			if (
				selectedNodeFrom.type === schema.nodes.paragraph &&
				selectedNodeFrom.content.size === 0
			) {
				tr = tr.delete(sel.from - 1, sel.to + 1);
			}

			view.dispatch(tr);
			setTimeout(() => view.focus(), 0);
		};

		document.addEventListener(INSERT_TLDRAW_EMBED_EVENT_ID, handler);
		return () => document.removeEventListener(INSERT_TLDRAW_EMBED_EVENT_ID, handler);
	});

	// Focus the editor when it's first rendered.
	useEditorEffect(
		(view) => {
			view.focus();
			if (isRunningInIframe()) {
				sendPostMessage("iframeReady");
			}
		},
		[query]
	);

	useEditorEffect((view) => {
		// FIXME: Yes yes this is very lame. The alternative is drilling it down into the plugins
		// layer, but that is called in many places which do not know about the publishTemplate.
		if (publishTemplate !== "tech-docs-vitepress") {
			return;
		}
		view.dom.classList.add("no-margin");
	});

	return (
		<>
			<ProseMirrorDoc ref={editorContainerRef} />
			<BottomMargin />

			{/* {isEnabled(Flags.Comments) && (
					<CellCommentBubbles parentEl={editorContainerRef} />
				)} */}
		</>
	);
});

Editor.displayName = "Editor";

export const nodeViews = {
	compute_cell: ComputeCell,
	code_block: CodeBlockWrapper,
	to_do_item: ToDoItem,
	image: Image,
	inline_image: InlineImage,
	pull_request: LinkPullRequest,
	code_snippet: LinkCodeSnippet,
	code_snippet_block: CodeSnippetBlock,
	tldraw_embed: TldrawEmbedWrapper,
	page_link: PageLink,
	...((env.isDesktopApp || env.isMobileApp) && { columns: Columns }),
};

const EditorWrapper: React.FC<EditorProps> = ({
	document,
	editorState,
	documentInteractionMode = "interactive",
	...props
}) => {
	const { workspaceName } = useWorkspaceFromCurrentRoute();
	const { documentId, isEditable } = useDocumentFromCurrentRoute();
	const { pageId } = usePageFromCurrentRoute();

	const collabEnabled = !!collabKey.getState(editorState);
	const { isAuthenticated } = useAuth0();

	// If we're in collaborative mode but not logged in, halt the editor
	const hasCollabAuthError = collabEnabled && !isAuthenticated;
	const hasCollabError = hasCollabAuthError;
	const hasDocMetadataPersistenceError = false;

	const hasPagePersistenceError = false;
	const hasPersistenceError = hasDocMetadataPersistenceError || hasPagePersistenceError;

	useEffect(() => {
		if (hasCollabAuthError) {
			makeToast({
				message:
					"You must be logged in to collaboratively edit this document. Please log in to continue editing.",
				variant: "error",
				timer: "dismissible",
			});
		}
	}, [hasCollabAuthError]);

	const { applyTransaction } = useEditorStateForCurrRoute();

	const { indicators: mappedPresenceIndicators } = usePresence({ documentId, pageId });

	const router = useRouter();

	// Cache link decorations to avoid full document traversal on every presence update.
	const pagesMetadata = document.pagesMetadata;
	const linkDecorationConfig = useMemo(
		() => ({
			workspaceName,
			documentId,
			currentPageId: pageId,
			publishSettings: document.publishSettings,
			pagesMetadata,
		}),
		[workspaceName, documentId, pageId, document.publishSettings, pagesMetadata]
	);
	const linkDecoCacheRef = useRef<{
		config: typeof linkDecorationConfig;
		docId: unknown;
		decos: Decoration[];
	} | null>(null);

	const decorations = useCallback(
		(state: EditorState) => {
			const decos: Decoration[] = [];

			// Reuse cached link decorations if document hasn't changed
			const docId = state.doc;
			const cache = linkDecoCacheRef.current;
			if (cache && cache.docId === docId && cache.config === linkDecorationConfig) {
				decos.push(...cache.decos);
			} else {
				// Full traversal only when document changes
				const linkDecos: Decoration[] = [];
				state.doc.descendants((node, pos) => {
					const linkMark = schema.marks.link.isInSet(node.marks);
					if (!linkMark) {
						return true;
					}

					const href = linkMark.attrs["href"] as string;
					let resolvedHref: string | null = null;

					if (href.startsWith("page:")) {
						const pages = linkDecorationConfig.pagesMetadata;
						// Skip resolution if pagesMetadata hasn't loaded yet
						if (!pages) {
							return true;
						}
						const urlPrefix = makeUrlPrefix(
							linkDecorationConfig.workspaceName,
							linkDecorationConfig.documentId,
							linkDecorationConfig.publishSettings
						);
						resolvedHref = resolvePageHref(
							href,
							pages,
							urlPrefix,
							linkDecorationConfig.currentPageId
						);
					} else if (href.startsWith("/d/")) {
						// Legacy /d/ links - rewrite workspace prefix
						const urlPrefix = makeUrlPrefix(
							linkDecorationConfig.workspaceName,
							linkDecorationConfig.documentId,
							linkDecorationConfig.publishSettings
						);
						resolvedHref = href.replace(/^\/d\/local\/.*?\//, `${urlPrefix}/`);
					}

					if (resolvedHref) {
						let target = "_self";
						if (isRunningInIframe()) {
							target = "_parent";
						}

						linkDecos.push(
							Decoration.inline(pos, pos + node.nodeSize, {
								nodeName: "a",
								href: resolvedHref,
								target,
								title: linkMark.attrs["title"],
							})
						);
					}

					return true;
				});
				linkDecoCacheRef.current = {
					config: linkDecorationConfig,
					docId,
					decos: linkDecos,
				};
				decos.push(...linkDecos);
			}

			// Presence decorations - no traversal needed
			if (mappedPresenceIndicators) {
				for (const indicator of mappedPresenceIndicators) {
					const { anchor, head } = indicator;
					if (!indicatorDisplayableInProseMirror(state, indicator)) {
						continue;
					}
					decos.push(
						Decoration.inline(Math.min(anchor, head), Math.max(anchor, head), {
							style: `background-color: ${getPresenceColor(indicator.userId)}50;`,
						})
					);
				}
			}

			return DecorationSet.create(state.doc, decos);
		},
		[linkDecorationConfig, mappedPresenceIndicators]
	);

	const send = useSendToCodeEditorStore();

	const dropHandler = useEaselProseMirrorDropHandler();
	const pasteHandler = useEaselProseMirrorPasteHandler();
	const copyHandler = useEaselProseMirrorCopyHandler();

	return (
		<ProseMirror
			key={pageId}
			state={editorState}
			attributes={{
				id: SCROLL_CONTAINER_ROOT_ID,
				class: `group/prosemirror rich-text${!env.isDesktopApp && !env.isMobileApp && document.publishSettings?.template === "tech-docs-vitepress" ? " no-margin" : ""}`,
			}}
			decorations={decorations}
			nodeViews={nodeViews}
			handlePaste={pasteHandler}
			transformPasted={(slice, _view) => {
				/* We remove images with data srcs because they can be so large
				 * that the document isn't safe to save in dynamo. Note that
				 * even if we move storage to a different store we still probably
				 * want to remove very large data src uris since they will make
				 * document loading difficult. Instead we shuold be using our
				 * image uploader to convert these to images in s3 and replace
				 * them with an s3 url.
				 */
				const f = removeDataSrcImagesFromFragment(slice.content);
				return new Slice(f, slice.openStart, slice.openEnd);
			}}
			editable={() => {
				/* Static mode is always read-only (e.g., template previews, SSR pages) */
				if (documentInteractionMode === "static") {
					return false;
				}
				/* If we are in the local workspace, we are always editable because
				 * it means the files are on disk (unless there's an unrecoverable error)
				 */
				return isEditable && !hasCollabError && !hasPersistenceError;
			}}
			handleDrop={dropHandler}
			handleDOMEvents={{
				copy: copyHandler,
				focus: (view, _event) => {
					//
					// VERY IMPORTANT: Do NOT race with page title changes. This is among the
					// lamest things that we have ever done. Here's what happens:
					//
					//   1. Click "New Page."
					//   2. Make a title for the new page.
					//   3. Do NOT press enter or click anywhere else.
					//   4. While the title is still being edited, click the canvas.
					//   5. We will replace the placeholder and then immediately a bunch of
					//      other transactions will be fired off. They do things like
					//      un-highlight comments.
					//   6. Because we just replaced the document placeholder, all those
					//      transactions will conflict.
					//
					// To avoid all this, we simply replace the placeholder in a few hundred
					// milliseconds.
					//

					setTimeout(() => {
						const { tr } = view.state;

						focusEditor({
							view: view as unknown as EditorView,
							send,
							activePageID: pageId,
						});

						// Assemble and dispatch a single transaction
						setTextPlaceholderStateFocused(tr);
						view.dispatch(tr);
					}, 200);
					return false;
				},
				blur: (view) => {
					const { tr } = view.state;

					// Assemble and dispatch a single transaction
					setTextPlaceholderStateBlurred(tr);
					view.dispatch(tr);
					return false;
				},
				mouseover: (_view, _event) => {
					// controlSetters.mouse(event);
					return false;
				},
				click: (view, event) => {
					const target = event.target as HTMLElement;

					const sel = view.state.selection;

					//
					// IMPORTANT: WebKit bug. A user has a cursor inside a `code_block` and clicks
					// the empty space after the paragraph immediately before it, but before the
					// `code_block`.
					//
					// Ultimately this is because Safari/WebKit reports that the selection is the
					// CodeMirror editor div. From ProseMirror's perspective, the selection has not
					// actually chagned, so the ProseMirror view short circuits and does not update
					// the selection. So, we have to MANUALLY update this IF AND ONLY IF we are
					// moving betwween code blocks.
					//
					if (
						target.tagName === "P" &&
						sel.anchor === sel.head &&
						sel.$anchor.node().type === schema.nodes.code_block
					) {
						const pos = view.posAtDOM(target, 0);
						if (pos !== null && pos !== undefined) {
							const $pos = view.state.doc.resolve(pos);
							const tr = view.state.tr;

							// Set selection to the end of the paragraph.
							const endPos = tr.doc.resolve($pos.end($pos.depth));
							tr.setSelection(TextSelection.near(endPos, -1));

							view.dispatch(tr.scrollIntoView());
							return true;
						}
					}

					// if the target is not anchor tag, we don't care to handle it
					if (target.tagName !== "A") {
						return false;
					}
					const href: string | null =
						target.getAttribute("href") || target.getAttribute("data-href");
					// if the href is a local link, we want to handle it
					if (href?.startsWith("/d/")) {
						if (isRunningInIframe()) {
							void handleIframeNavigation(
								event,
								{
									workspaceName,
									documentId,
									pageSlug: href.split("/").pop()!,
								},
								router
							);
						} else {
							void router.push(href);
						}
					}

					return false;
				},
			}}
			handleClickOn={(_view, _pos, node) => {
				if (node.type === schema.nodes.compute_cell) {
					return true;
				}

				return false;
			}}
			dispatchTransaction={(tr) => {
				// pass transactions back up to Easel
				// this is needed to make sure that the editor state is updated when things like "selections" are updated
				void applyTransaction(tr);
			}}
		>
			{props.children}
			<PageLinkSuggestions />
		</ProseMirror>
	);
};

export type BigRichTextEditorProps = PropsWithChildren<{
	document: DocumentMetadata;
	editorState: EditorState;
	documentInteractionMode?: "static" | "interactive";
}>;

const BigRichTextEditorInterop: React.FC<BigRichTextEditorProps> = ({
	document,
	editorState,
	documentInteractionMode = "interactive",
	...props
}) => {
	const { documentId } = useDocumentFromCurrentRoute();
	const { pageId } = usePageFromCurrentRoute();

	const runtime = useCreateRuntime({
		documentId,
		pageId: pageId ?? "",
		documentInteractionMode,
	});

	return (
		<RuntimeProvider runtime={runtime}>
			{createElement(EditorWrapper, {
				...props,
				document,
				editorState,
				documentInteractionMode,
			})}
		</RuntimeProvider>
	);
};

export const BigRichTextEditor = React.memo(BigRichTextEditorInterop);
